import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Images } from "lucide-react";
import {
  useAdminAuth,
  AdminLogin,
  AdminLoading,
  ConfirmDelete,
} from "./AdminAuth";
import { DeleteBtn } from "./AdminListPage";
import AdminBar from "./AdminBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-sm border border-[#102C1F]/25 bg-[#F4F1EB] px-4 py-2.5 text-sm text-[#102C1F] outline-none transition-colors duration-300 placeholder:text-[#102C1F]/40 focus:border-[#102C1F]";

const labelCls =
  "mb-1.5 block text-xs uppercase tracking-[0.2em] text-[#102C1F]/60";

export default function AdminPortfolioPage() {
  const [user, setUser] = useAdminAuth();
  const [photos, setPhotos] = useState([]);
  const [toDelete, setToDelete] = useState(null);
  const [uploading, setUploading] = useState({});

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/gallery`);
    setPhotos(data);
  }, []);

  useEffect(() => {
    if (!user) return;
    load().catch(() => {});
  }, [user, load]);

  const updatePhoto = (i, key, val) =>
    setPhotos((prev) =>
      prev.map((x, j) => (j === i ? { ...x, [key]: val } : x))
    );

  const addPhoto = () =>
    setPhotos((prev) => [...prev, { id: "", name: "", img: "" }]);

  const removePhoto = (p, i) => {
    if (!p.id) {
      setPhotos((prev) => prev.filter((_, j) => j !== i));
      return;
    }
    setToDelete({ id: p.id, name: p.name || "cette photo" });
  };

  const savePhoto = async (p, i) => {
    if (!p.img.trim()) {
      toast.error("Téléchargez une photo ou collez une URL.");
      return;
    }
    try {
      if (!p.id) {
        const { data } = await axios.post(
          `${API}/gallery`,
          { name: p.name, img: p.img },
          { withCredentials: true }
        );
        setPhotos((prev) => prev.map((x, j) => (j === i ? data : x)));
        toast.success("Photo ajoutée !");
      } else {
        await axios.put(
          `${API}/gallery/${p.id}`,
          { name: p.name, img: p.img },
          { withCredentials: true }
        );
        toast.success("Photo mise à jour !");
      }
    } catch {
      toast.error("Impossible d'enregistrer. Reconnectez-vous.");
    }
  };

  const uploadPhoto = async (i, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading((p) => ({ ...p, [i]: 0 }));
    try {
      const { data } = await axios.post(`${API}/upload-image`, fd, {
        withCredentials: true,
        onUploadProgress: (e) =>
          setUploading((p) => ({
            ...p,
            [i]: Math.round((e.loaded * 100) / (e.total || 1)),
          })),
      });
      updatePhoto(i, "img", data.url);
      toast.success("Photo téléchargée ! Pensez à Enregistrer.");
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Impossible d'envoyer l'image."
      );
    } finally {
      setUploading((p) => {
        const n = { ...p };
        delete n[i];
        return n;
      });
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await axios.delete(`${API}/gallery/${toDelete.id}`, {
        withCredentials: true,
      });
      setPhotos((prev) => prev.filter((p) => p.id !== toDelete.id));
      toast.success("Supprimé.");
    } catch {
      toast.error("Impossible de supprimer.");
    }
    setToDelete(null);
  };

  if (user === null) return <AdminLoading />;

  if (user === false) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F1EB] px-5">
        <AdminLogin onSuccess={setUser} title="Portfolio" />
      </div>
    );
  }

  return (
    <div
      data-testid="admin-portfolio-page"
      className="min-h-screen bg-[#F4F1EB] text-[#102C1F]"
    >
      <AdminBar />
      <header className="bg-[#102C1F] text-[#F4F1EB]">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-10">
          <span className="flex items-center gap-3 font-display text-xl italic md:text-2xl">
            <Images size={20} /> Portfolio
          </span>
          <Link
            data-testid="portfolio-dashboard-link"
            to="/admin"
            className="rounded-full border border-[#F4F1EB]/30 px-4 py-2 text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:bg-[#F4F1EB] hover:text-[#102C1F]"
          >
            Gestion
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-12 md:px-10">
        <button
          data-testid="portfolio-photo-add-btn"
          onClick={addPhoto}
          className="mb-6 rounded-full border border-dashed border-[#102C1F]/40 px-6 py-3 text-xs uppercase tracking-[0.25em] text-[#102C1F]/70 transition-colors duration-300 hover:border-[#102C1F] hover:text-[#102C1F]"
        >
          + Ajouter une photo
        </button>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {photos.map((p, i) => (
            <div
              key={p.id || `new-${i}`}
              data-testid={`portfolio-photo-card-${i}`}
              className="relative rounded-sm border border-[#102C1F]/15 bg-[#EDE8DE] p-4"
            >
              <span className="absolute right-3 top-3 z-10 rounded-full bg-[#F4F1EB]/90">
                <DeleteBtn
                  testid={`portfolio-photo-delete-${i}`}
                  onClick={() => removePhoto(p, i)}
                />
              </span>
              {p.img ? (
                <img
                  src={p.img}
                  alt={p.name || "Photo"}
                  className="mb-4 aspect-[4/5] w-full rounded-sm object-cover"
                />
              ) : (
                <div className="mb-4 flex aspect-[4/5] w-full items-center justify-center rounded-sm border border-dashed border-[#102C1F]/25 text-[#102C1F]/40">
                  <Images size={32} />
                </div>
              )}
              <div className="space-y-3">
                <div className="block">
                  <span className={labelCls}>Photo</span>
                  <label
                    data-testid={`portfolio-photo-upload-${i}`}
                    className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-[#102C1F]/30 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-[#102C1F]/70 transition-colors duration-300 hover:bg-[#102C1F] hover:text-[#F4F1EB]"
                  >
                    <input
                      data-testid={`portfolio-photo-file-${i}`}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        uploadPhoto(i, e.target.files[0]);
                        e.target.value = "";
                      }}
                    />
                    {uploading[i] != null
                      ? `Envoi ${uploading[i]}%`
                      : "Télécharger une photo"}
                  </label>
                  <input
                    data-testid={`portfolio-photo-img-${i}`}
                    value={p.img}
                    placeholder="ou collez une URL https://..."
                    onChange={(e) => updatePhoto(i, "img", e.target.value)}
                    className={`${field} mt-2`}
                  />
                </div>
                <label className="block">
                  <span className={labelCls}>Légende (facultatif)</span>
                  <input
                    data-testid={`portfolio-photo-name-${i}`}
                    value={p.name}
                    placeholder="Ex : Poulet grillé"
                    onChange={(e) => updatePhoto(i, "name", e.target.value)}
                    className={field}
                  />
                </label>
                <button
                  data-testid={`portfolio-photo-save-${i}`}
                  onClick={() => savePhoto(p, i)}
                  className="rounded-full bg-[#102C1F] px-6 py-2.5 text-xs uppercase tracking-[0.25em] text-[#F4F1EB] transition-colors duration-300 hover:bg-[#1B4030]"
                >
                  {p.id ? "Enregistrer" : "Ajouter"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </main>

      <ConfirmDelete
        item={toDelete}
        onCancel={() => setToDelete(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
